import React, { createContext, ReactNode, useState } from "react";
import { ErrorTypeEnum } from "src/components/Form/types";
import {
  RequestStatusEnum,
  RequestTypeEnum,
} from "src/components/Changes/types";
import { AlertTones } from "@origin-digital/ods-types";

interface IChangeRequestContext {
  displayingSnackbar: {
    message: string;
    tone: AlertTones;
  } | null;
  setDisplayingSnackbar: React.Dispatch<
    React.SetStateAction<{
      message: string;
      tone: AlertTones;
    } | null>
  >;
  premiseAddress: string | null;
  setPremiseAddress: React.Dispatch<React.SetStateAction<string | null>>;
  displayingAlertBanner: ErrorTypeEnum | null;
  setDisplayingAlertBanner: React.Dispatch<
    React.SetStateAction<ErrorTypeEnum | null>
  >;
  draftInfo: DraftInfo | null;
  setDraftInfo: React.Dispatch<React.SetStateAction<DraftInfo | null>>;
  activeSection: string;
  setActiveSection: React.Dispatch<React.SetStateAction<string>>;
  sectionVisibility: {
    meteringRequirementsSection: boolean;
    supportingDocumentsSection: boolean;
    solarInstallationDetailsSection: boolean;
  };
  setSectionVisibility: React.Dispatch<React.SetStateAction<SectionVisibility>>;
}

const ChangeRequestContext = createContext<IChangeRequestContext>({
  displayingSnackbar: null,
  setDisplayingSnackbar: () => {},
  displayingAlertBanner: null,
  setDisplayingAlertBanner: () => {},
  premiseAddress: null,
  setPremiseAddress: () => {},
  draftInfo: null,
  setDraftInfo: () => {},
  activeSection: "premiseSection",
  setActiveSection: () => {},
  sectionVisibility: {
    meteringRequirementsSection: false,
    supportingDocumentsSection: false,
    solarInstallationDetailsSection: false,
  },
  setSectionVisibility: () => {},
});

interface IChangeRequestProviderProps {
  children: ReactNode;
}

interface DraftInfo {
  draft_id: string;
  draft_version: string;
  request_id: string;
  raw_request_status: RequestStatusEnum;
  request_type: RequestTypeEnum;
  message_enabled: boolean;
}

export interface SectionVisibility {
  meteringRequirementsSection: boolean;
  supportingDocumentsSection: boolean;
  solarInstallationDetailsSection: boolean;
}

export const ChangeRequestProvider: React.FC<IChangeRequestProviderProps> = ({
  children,
}) => {
  const [displayingSnackbar, setDisplayingSnackbar] = useState<{
    message: string;
    tone: AlertTones;
  } | null>(null);
  const [premiseAddress, setPremiseAddress] = useState<string | null>(null);
  const [draftInfo, setDraftInfo] = useState<DraftInfo | null>(null);
  const [displayingAlertBanner, setDisplayingAlertBanner] =
    useState<ErrorTypeEnum | null>(null);
  const [activeSection, setActiveSection] = useState<string>("premiseSection");
  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(
    {
      meteringRequirementsSection: false,
      supportingDocumentsSection: false,
      solarInstallationDetailsSection: false,
    }
  );
  const contextValue: IChangeRequestContext = {
    displayingSnackbar,
    setDisplayingSnackbar,
    premiseAddress,
    setPremiseAddress,
    displayingAlertBanner,
    setDisplayingAlertBanner,
    draftInfo,
    setDraftInfo,
    activeSection,
    setActiveSection,
    sectionVisibility,
    setSectionVisibility,
  };

  return (
    <ChangeRequestContext.Provider value={contextValue}>
      {children}
    </ChangeRequestContext.Provider>
  );
};

export default ChangeRequestContext;
